import React, {
  useState,
  useRef,
  useEffect,
  forwardRef,
} from "react";
import { createPortal } from "react-dom";
import { TextField } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const MultiLineCreatableSelect = React.memo(
  forwardRef(
    (
      {
        options = [],
        value = "",
        multiline = true,
        selectedValues = [],
        onChange,
        onSelect,
        onBlur,
        onFocus,
        onMenuOpen,
        onMenuClose,
        placeholder = "Type or select...",
        menuPortalTarget = null,
        isDisabled = false,
        allowCreate = true,
      },
      ref
    ) => {
      const [input, setInput] = useState(value);
      const [open, setOpen] = useState(false);

      const wrapperRef = useRef(null);
      const dropdownRef = useRef(null);

      const [dropdownPosition, setDropdownPosition] =
        useState({
          top: 0,
          left: 0,
          width: 0,
        });

      // =========================
      // Sync external value
      // =========================
      useEffect(() => {
        setInput(value || "");
      }, [value]);

      // =========================
      // Close dropdown on outside click
      // =========================
      useEffect(() => {
        const handleClickOutside = (e) => {
          const isOutsideWrapper =
            wrapperRef.current &&
            !wrapperRef.current.contains(e.target);

          const isOutsideDropdown =
            menuPortalTarget &&
            dropdownRef.current &&
            !dropdownRef.current.contains(e.target);

          if (menuPortalTarget) {
            if (
              isOutsideWrapper &&
              isOutsideDropdown
            ) {
              setOpen(false);
              setInput(value || "");
            }
          } else {
            if (isOutsideWrapper) {
              setOpen(false);
              setInput(value || "");
            }
          }
        };

        document.addEventListener(
          "mousedown",
          handleClickOutside
        );

        return () => {
          document.removeEventListener(
            "mousedown",
            handleClickOutside
          );
        };
      }, [menuPortalTarget, value]);

      // =========================
      // Dropdown position
      // =========================
      useEffect(() => {
        if (open) {
          onMenuOpen?.();

          if (
            menuPortalTarget &&
            wrapperRef.current
          ) {
            const rect =
              wrapperRef.current.getBoundingClientRect();

            setDropdownPosition({
              top:
                rect.bottom +
                window.scrollY +
                4,
              left:
                rect.left +
                window.scrollX,
              width: rect.width,
            });
          }
        } else {
          onMenuClose?.();
        }
      }, [
        open,
        onMenuOpen,
        onMenuClose,
        menuPortalTarget,
      ]);

      // =========================
      // Update position on scroll
      // =========================
      useEffect(() => {
        if (!open || !menuPortalTarget) return;

        let frameId = null;

        const updatePosition = () => {
          if (!wrapperRef.current) return;

          const rect =
            wrapperRef.current.getBoundingClientRect();

          setDropdownPosition((prev) => {
            const next = {
              top:
                rect.bottom +
                window.scrollY +
                4,
              left:
                rect.left +
                window.scrollX,
              width: rect.width,
            };

            // Prevent unnecessary rerenders
            if (
              prev.top === next.top &&
              prev.left === next.left &&
              prev.width === next.width
            ) {
              return prev;
            }

            return next;
          });

          frameId = null;
        };

        const handleScroll = () => {
          if (frameId) return;

          frameId =
            requestAnimationFrame(updatePosition);
        };

        window.addEventListener(
          "scroll",
          handleScroll,
          true
        );

        window.addEventListener(
          "resize",
          handleScroll
        );

        return () => {
          window.removeEventListener(
            "scroll",
            handleScroll,
            true
          );

          window.removeEventListener(
            "resize",
            handleScroll
          );

          if (frameId) {
            cancelAnimationFrame(frameId);
          }
        };
      }, [open, menuPortalTarget]);

      // =========================
      // Filtered options
      // Always show selected option
      // =========================
      const filteredOptions = options.filter((opt) => {
        if (
          value === opt ||
          selectedValues.includes(opt)
        ) {
          return true;
        }

        return opt
          .toLowerCase()
          .includes(input.toLowerCase());
      });

      const normalizedInput = input
        .replace(/\s+/g, "")
        .toLowerCase();

      const showCreateOption =
        allowCreate &&
        normalizedInput &&
        !options.some(
          (opt) =>
            opt
              .replace(/\s+/g, "")
              .toLowerCase() ===
            normalizedInput
        );

      // =========================
      // Handlers
      // =========================
      const handleSelect = (opt) => {
        setInput(opt);
        setOpen(false);

        onChange?.(opt);
        onSelect?.(opt);
      };

      // const handleInputChange = (e) => {
      //   const val = e.target.value;

      //   setInput(val);
      //   setOpen(true);
      // };

      const handleInputChange = (e) => {
        if (!allowCreate) return;
        let val = e.target.value;
        val = val.replace(/\r\n/g, "\n");
        setInput(val);
        setOpen(true);
        onChange?.(val);
      };

      const handleBlur = () => {
        setOpen(false);

        // Restore selected value
        setInput(value || "");

        onBlur?.();
      };

      const handleFocus = (e) => {
        if (!isDisabled) {
          setOpen(true);
        }

        onFocus?.(e);
      };

      const handleClear = (e) => {
        e.stopPropagation();

        setInput("");

        onChange?.("");
        onSelect?.("");
      };

      const toggleOpen = () => {
        if (!isDisabled) {
          setOpen((prev) => !prev);
        }
      };

      // =========================
      // DROPDOWN
      // =========================
      const renderDropdown = () => (
        <div
          ref={dropdownRef}
          style={{
            position: "absolute",
            willChange: "transform",
            transform: "translateZ(0)",
            backfaceVisibility: "hidden",
            top: menuPortalTarget
              ? dropdownPosition.top
              : "100%",
            left: menuPortalTarget
              ? dropdownPosition.left
              : 0,
            right: menuPortalTarget
              ? undefined
              : 0,
            width: menuPortalTarget
              ? dropdownPosition.width
              : "100%",
            marginTop: menuPortalTarget
              ? 0
              : 4,
            background: "#fff",
            border:
              "1px solid hsl(0, 0%, 80%)",
            borderRadius: 4,
            maxHeight: 170,
            overflowY: "auto",
            zIndex: 9999,
            boxShadow:
              "0 4px 12px rgba(0,0,0,0.15)",
          }}
        >
          {filteredOptions.map((opt) => {
            const selected =
              selectedValues.includes(opt) ||
              value === opt;

            return (
              <div
                key={opt}
                onMouseDown={(e) =>
                  e.preventDefault()
                }
                onClick={() => {
                  if (!selected) {
                    handleSelect(opt);
                  }
                }}
                onMouseEnter={(e) => {
                  if (!selected) {
                    e.currentTarget.style.backgroundColor =
                      "#DEEBFF";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!selected) {
                    e.currentTarget.style.backgroundColor =
                      "transparent";
                  }
                }}
                style={{
                  padding: "8px 12px",
                  fontSize: "14px",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",

                  backgroundColor: selected
                    ? "#2684FF"
                    : "transparent",

                  color: selected
                    ? "#FFFFFF"
                    : "#333",

                  fontWeight: selected
                    ? 500
                    : 400,

                  cursor: selected
                    ? "default"
                    : "pointer",

                  pointerEvents: "auto",

                  transition:
                    "background-color 0.2s",
                }}
              >
                {opt}
              </div>
            );
          })}

          {filteredOptions.length === 0 &&
            !showCreateOption && (
              <div
                style={{
                  padding: "10px 12px",
                  fontSize: "14px",
                  color: "#999",
                  textAlign: "center",
                  backgroundColor: "#fff",
                }}
              >
                No options
              </div>
            )}
          {showCreateOption && (
            <div
              key="__create__"
              onMouseDown={(e) =>
                e.preventDefault()
              }
              onClick={() => handleSelect(input)}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor =
                  "#DEEBFF";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor =
                  "transparent";
              }}
              style={{
                padding: "8px 12px",
                cursor: "pointer",
                fontSize: "14px",
                color: "#1976d2",
                fontWeight: 600,
                borderTop:
                  filteredOptions.length > 0
                    ? "1px solid #f0f0f0"
                    : "none",

                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                overflowWrap: "break-word",
                lineHeight: "1.4",
              }}
            >
              Create "{input}"
            </div>
          )}
        </div>
      );

      return (
        <div
          ref={wrapperRef}
          style={{
            position: "relative",
            width: "100%",
          }}
        >
          <div
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
            }}
          >
            <TextField
              disabled={isDisabled}
              inputRef={ref}
              value={input}
              placeholder={placeholder}
              onChange={handleInputChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              inputProps={{
                readOnly: !allowCreate,
              }}
              onClick={() => {
                if (!isDisabled) {
                  setOpen(true);
                }
              }}
              multiline={multiline}
              minRows={1}
              variant="outlined"
              size="small"
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  alignItems: "flex-center",
                  padding:
                    "6px 55px 6px 6px",
                  borderRadius: "4px",
                  backgroundColor: isDisabled
                    ? "#f5f5f5"
                    : "#fff",
                  minHeight: "38px",
                  width: "170px",
                  cursor: isDisabled
                    ? "not-allowed"
                    : "pointer",
                },

                "& .MuiOutlinedInput-input": {
                  fontSize: "12px",
                  padding: multiline ? "4px 0" : 0,
                  lineHeight: multiline ? "1.4" : "normal",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                  cursor: isDisabled
                    ? "not-allowed"
                    : "text",
                },

                "& .Mui-disabled": {
                  cursor: "not-allowed",
                },
              }}
            />

            {/* Right Controls */}
            <div
              style={{
                position: "absolute",
                right: 4,
                top: "50%",
                transform:
                  "translateY(-50%)",
                display: "flex",
                alignItems: "center",
                height: "100%",
              }}
            >
              {input && !isDisabled && (
                <div
                  onMouseDown={(e) =>
                    e.preventDefault()
                  }
                  onClick={handleClear}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: "#ccc",
                    padding: "0 3px",
                    height: "100%",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color =
                      "#aaa";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color =
                      "#ccc";
                  }}
                >
                  <CloseIcon
                    style={{ fontSize: 16 }}
                  />
                </div>
              )}

              <div
                style={{
                  width: "1px",
                  height: "16px",
                  backgroundColor: "#d8d8d8",
                }}
              />

              <div
                onMouseDown={(e) =>
                  e.preventDefault()
                }
                onClick={toggleOpen}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: isDisabled
                    ? "not-allowed"
                    : "pointer",
                  color: "#999",
                  height: "100%",
                }}
              >
                <KeyboardArrowDownIcon
                  sx={{
                    fontSize: 18,
                    transition:
                      "transform 0.2s",
                    transform: open
                      ? "rotate(180deg)"
                      : "rotate(0deg)",

                    "&:hover": {
                      color: "#666",
                    },
                  }}
                />
              </div>
            </div>
          </div>

          {/* {open &&
          (filteredOptions.length > 0 ||
            showCreateOption) &&
          (menuPortalTarget
            ? createPortal(
                renderDropdown(),
                menuPortalTarget
              )
            : renderDropdown())} */}
          {open &&
            (menuPortalTarget
              ? createPortal(
                renderDropdown(),
                menuPortalTarget
              )
              : renderDropdown())}
        </div>
      );
    }
  )
);

export default MultiLineCreatableSelect;